import { useEffect, useState } from 'react'
import { useMedplum } from '@medplum/react'
import type { ValueSet } from '@medplum/fhirtypes'
import { logError } from './errorUtils'

export interface CptCode {
  code: string
  display: string
}

const VALUESET_URL_BASE = 'https://behuman.health/ValueSet/cpt-for-'

function extractConcepts(valueSet: ValueSet): CptCode[] {
  const concepts = valueSet.compose?.include?.[0]?.concept ?? []
  return concepts
    .map((c) => ({ code: c.code ?? '', display: c.display ?? '' }))
    .filter((c) => c.code)
}

export function useCptValueSet(specialtyCode: string | null) {
  const medplum = useMedplum()
  const [codes, setCodes] = useState<CptCode[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!specialtyCode) {
      setCodes([])
      setLoading(false)
      return
    }

    let mounted = true
    setLoading(true)
    setError(null)

    const url = `${VALUESET_URL_BASE}${specialtyCode}`
    medplum
      .searchResources('ValueSet', `url=${encodeURIComponent(url)}&_count=1`)
      .then((results) => {
        if (!mounted) return
        const vs = results[0]
        if (!vs) {
          setCodes([])
          return
        }
        setCodes(extractConcepts(vs))
      })
      .catch((err: unknown) => {
        if (!mounted) return
        logError('CPT ValueSet fetch', err)
        setError(err instanceof Error ? err : new Error(String(err)))
        setCodes([])
      })
      .finally(() => {
        if (mounted) setLoading(false)
      })

    return () => {
      mounted = false
    }
  }, [medplum, specialtyCode])

  return { codes, loading, error }
}
